import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from '@/servicios/auth'
import { api } from '@/servicios/api'
import { PaginaEncuesta } from '@/paginas/publico/Encuesta'
import { RutaProtegida } from '@/paginas/backoffice/RutaProtegida'
import { Espera } from '@/componentes/ui'

// El backoffice (gráficos, mapas administrativos, etc.) se separa del
// formulario público en su propio chunk: quien abre un enlace QR nunca
// descarga ese código — importa para redes móviles lentas (riesgo de
// conectividad, ver docs/PLAN.md §6).
const PaginaLogin = lazy(() => import('@/paginas/backoffice/Login').then((m) => ({ default: m.PaginaLogin })))
const LayoutBackoffice = lazy(() => import('@/paginas/backoffice/Layout').then((m) => ({ default: m.LayoutBackoffice })))
const PaginaTablero = lazy(() => import('@/paginas/backoffice/Tablero').then((m) => ({ default: m.PaginaTablero })))
const PaginaRespuestas = lazy(() => import('@/paginas/backoffice/Respuestas').then((m) => ({ default: m.PaginaRespuestas })))
const PaginaRespuestaDetalle = lazy(() => import('@/paginas/backoffice/RespuestaDetalle').then((m) => ({ default: m.PaginaRespuestaDetalle })))
const PaginaCampanas = lazy(() => import('@/paginas/backoffice/Campanas').then((m) => ({ default: m.PaginaCampanas })))
const PaginaEnlaces = lazy(() => import('@/paginas/backoffice/Enlaces').then((m) => ({ default: m.PaginaEnlaces })))

function Portada() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-2xl font-bold text-azul">RUMBO</h1>
      <p className="max-w-md text-texto-suave">
        Encuesta de Movilidad Estudiantil — Ministerio del Poder Popular para el Transporte. Use el enlace o el código QR de su institución para responder, o
        entre al{' '}
        <a className="font-medium text-azul underline" href="/app">
          panel
        </a>{' '}
        si es personal autorizado.
      </p>
    </div>
  )
}

export function App() {
  const { cargando, establecerSesion, cerrarSesion } = useAuth()
  const esBackoffice = useLocation().pathname.startsWith('/app')

  // Al cargar el backoffice, intenta renovar la sesión con el refresh token
  // (cookie) para no exigir un login nuevo en cada visita. NO se intenta en
  // "/" ni en "/e/:token": quien escanea un QR de la encuesta pública nunca
  // tuvo sesión de backoffice, así que esa llamada estaba garantizada a dar
  // 403 en cada respuesta — tráfico y ruido de consola innecesarios a escala.
  useEffect(() => {
    if (!esBackoffice) return
    api('/auth/refresh', { metodo: 'POST', cuerpo: {} })
      .then((r) => establecerSesion(r as never))
      .catch(() => cerrarSesion())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esBackoffice])

  if (esBackoffice && cargando) return <Espera etiqueta="Iniciando RUMBO…" />

  return (
    <Routes>
      <Route path="/" element={<Portada />} />
      <Route path="/e/:token" element={<PaginaEncuesta />} />
      <Route
        path="/app/entrar"
        element={
          <Suspense fallback={<Espera etiqueta="Cargando…" />}>
            <PaginaLogin />
          </Suspense>
        }
      />
      <Route
        path="/app"
        element={
          <RutaProtegida>
            <Suspense fallback={<Espera etiqueta="Cargando…" />}>
              <LayoutBackoffice />
            </Suspense>
          </RutaProtegida>
        }
      >
        <Route index element={<Navigate to="tablero" replace />} />
        <Route path="tablero" element={<PaginaTablero />} />
        <Route path="respuestas" element={<PaginaRespuestas />} />
        <Route path="respuestas/:id" element={<PaginaRespuestaDetalle />} />
        <Route path="campanas" element={<PaginaCampanas />} />
        <Route path="enlaces" element={<PaginaEnlaces />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
